import * as Comlink from 'comlink';
import { useState, useCallback, useEffect, useRef } from 'react';

// The workerUsage map ensures there are no more workers in use prior to
// cleaning up the worker instance and comlink proxy.
const workerUsage = new Map();

const useWorker = ({ workerInstance }) => {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState();
  const workerRef = useRef(null);
  const proxyRef = useRef(null);
  const isBusy = useRef(false);

  // Sets up a worker instance and wraps it in the Comlink proxy;
  const setupWorker = useCallback(() => {
    if (!workerInstance || workerRef?.current) {
      return;
    }

    workerRef.current = workerInstance;

    // Initialize usage tracking if not present.
    if (!workerUsage.has(workerInstance)) {
      workerUsage.set(workerInstance, {
        count: 0,
        proxy: Comlink.wrap(workerInstance),
      });
    }

    workerUsage.get(workerInstance).count += 1;
    proxyRef.current = workerUsage.get(workerInstance).proxy;

    workerRef.onerror = (error) => {
      setError(error.message);
      setIsLoading(false);
      isBusy.current = false;
    };
  }, [workerInstance]);

  // Cleans up the worker instance and Comlink proxy if they are no longer in
  // use.
  const cleanupWorker = useCallback(() => {
    if (workerRef?.current && !isBusy.current) {
      const usage = workerUsage.get(workerInstance);

      if (usage) {
        usage.count -= 1;
        if (usage.count === 0) {
          try {
            usage.proxy[Comlink.releaseProxy]();
          } catch (error) {
            console.error('Error releasing proxy: ', error);
          }
          try {
            workerRef.current.terminate();
          } catch (error) {
            console.error('Error terminating worker: ', error);
          }
          workerUsage.delete(workerInstance);
        }
      }
      workerRef.current = null;
      proxyRef.current = null;
    }
  }, [workerInstance]);

  useEffect(() => {
    setupWorker();

    return () => {
      // Remove the worker and Comlink proxy when the hook is destroyed.
      cleanupWorker();
    };
  }, [setupWorker, cleanupWorker]);

  // Passes the provided input data to the web worker function.
  const run = useCallback(
    async (input) => {
      // Set up the worker if it doesn't exist.
      if (!workerRef.current || !proxyRef.current) {
        setupWorker();
      }

      setIsLoading(true);
      isBusy.current = true;

      try {
        const workerResult = await proxyRef.current.run(input);
        setResult(workerResult);
        setIsLoading(false);
        isBusy.current = false;
      } catch (error) {
        setError(error.message);
        setIsLoading(false);
        isBusy.current = false;
      }
    },
    [setupWorker]
  );

  return {
    error,
    isLoading,
    result,
    run,
  };
};

export default useWorker;
